import { SposConverter } from './spos-converter';
import { NacosConverter } from './nacos-converter';
import { RTEConverter } from './rte-converter';
import { RTZConverter, RTZVersion } from './rtz-converter';
import { SperryMVMConverter } from './sperrymvm-converter';
import { JRCConverter } from './jrc-converter';
import { VesselsPlanningConverter } from './vessels-planning-converter';
import { RouteGeometry, VoyageRoute, VoyageRouteLineFeature, VoyageRoutePointFeature } from '../models/voyage-routing-model';
import { findLastIndex } from '../utils/helpers';
import { getLegTypeFromDist } from '../utils/map-utils';
import { EVENT_CATEGORIES, TraceService } from '../services/trace-service';
import { VoyagePlan } from '../models/vessel-planning-model';
import { CoordinatesUtils } from '../utils/coordinates-utils';
import { Format, ExportResult, ImportResult } from './types/converters.types';
import { getPilotPointIndeces } from '../utils/route-utils';
import { keepOutermostPilotPoints } from './helpers';

export { SposConverter as Spos };
export { NacosConverter as Nacos };
export { RTEConverter as RTE };
export { RTZConverter as RTZ };
export { SperryMVMConverter as SperryMVM };
export { JRCConverter as JRC };
export { VesselsPlanningConverter as VesselsPlanning };

// Note: Filename argument is the filename not including the file extension 
export function exportPlan(plan: VoyagePlan, format: Format, filename: string): ExportResult
{
    if (TraceService)
    {
        TraceService.event(EVENT_CATEGORIES.ROUTE_IMPORT_EXPORT, "export", format);
    }

    switch (format)
    {
        case Format.SPOS:
            return { data: SposConverter.export(plan, Format.SPOS), mimeType: "text/xml", fileExt: "xml" };
        case Format.SPOS_Pilot:
            return { data: SposConverter.export(plan, Format.SPOS_Pilot), mimeType: "text/xml", fileExt: "xml" };
        case Format.Nacos:
            return { data: NacosConverter.export(plan), mimeType: "text/xml", fileExt: "xml" };
        case Format.RTE:
            throw new Error("RTE export is not implemented");
        case Format.RTZ_1_0:
            return { data: RTZConverter.export(plan, filename, RTZVersion.RTZ_1_0), mimeType: "text/xml", fileExt: "rtz" };
        case Format.RTZ_1_2:
            return { data: RTZConverter.export(plan, filename, RTZVersion.RTZ_1_2), mimeType: "text/xml", fileExt: "rtz" };
        case Format.Sperry:
            return { data: SperryMVMConverter.export(plan), mimeType: "text/xml", fileExt: "route" };
        case Format.JRC:
            return { data: JRCConverter.export(plan), mimeType: "text/csv", fileExt: "csv" };
        case Format.VoyagePlanning:
            return { data: VesselsPlanningConverter.export(plan), mimeType: "application/json", fileExt: "json" };
        default:
            throw new Error(`Unsupported format: ${format}`);
    }
}

export async function importFromFile(file: File, currentPlan?: VoyagePlan, mergeWith?: VoyageRoute): Promise<ImportResult>
{
    const str = await file.text();

    const result = importAndMerge(str, mergeWith, file.name);
    // remove extension from file name and assign to route.name
    result!.route.name = file.name.split('.').slice(0, -1).join('.');

    // pilot ETAs are mandatory. So if they are not present, we need to set them to future waypoints
    if (currentPlan) {
        const wpIndeces = getPilotPointIndeces(result!.route);
        if (wpIndeces.length > 0 && currentPlan.departurePilotEtd) {
            const departurePilot = result!.route.route_geometry[wpIndeces[0]];
            if (!departurePilot.properties.isPassed) {
                departurePilot.properties.eta = new Date(currentPlan.departurePilotEtd).toISOString();
            }
        }
        if (wpIndeces.length > 0 && currentPlan.arrivalPilotEta) {
            const arrivalPilot = result!.route.route_geometry[wpIndeces[wpIndeces.length - 1]];
            if (!arrivalPilot.properties.isPassed) {
                arrivalPilot.properties.eta = new Date(currentPlan.arrivalPilotEta).toISOString();
            }
        }
    }

    // Move properties from passage plan to route for old route type
    if (!result!.route.passagePlan) {
        if (currentPlan) {
            result!.route.passagePlan = {
                description: currentPlan.passagePlan.description,
                reminders: currentPlan.passagePlan.reminders,
                notEcdisCompliant: currentPlan.passagePlan.notEcdisCompliant,
                encCellsUsed: currentPlan.passagePlan.encCellsUsed,
                booksAndTables: currentPlan.passagePlan.booksAndTables,
                chartsAndLightsNotes: currentPlan.passagePlan.chartsAndLightsNotes,
                sailingDirections: currentPlan.passagePlan.sailingDirections,
                listOfRadioSignals: currentPlan.passagePlan.listOfRadioSignals,
                listOfLights: currentPlan.passagePlan.listOfLights,
                gpsEcdisRadarMap: currentPlan.passagePlan.gpsEcdisRadarMap,
                paperCharts: currentPlan.passagePlan.paperCharts,
                pubCorrectedToWeek: currentPlan.passagePlan.pubCorrectedToWeek,
                tideTables: currentPlan.passagePlan.tideTables,
                publicationsNotes: currentPlan.passagePlan.publicationsNotes,
                mastersOrders: currentPlan.passagePlan.mastersOrders,
                captain: currentPlan.passagePlan.captain,
                navigationalOfficer: currentPlan.passagePlan.navigationalOfficer,
                planDate: currentPlan.passagePlan.planDate,
                safetyMessages: {
                    navareaRegions: currentPlan.passagePlan.safetyMessages.navareaRegions,
                    navtexStations: currentPlan.passagePlan.safetyMessages.navtexStations
                },
                weatherInfo: {
                    navareaRegions: currentPlan.passagePlan.weatherInfo.navareaRegions,
                    navtexStations: currentPlan.passagePlan.weatherInfo.navtexStations,
                    info: currentPlan.passagePlan.weatherInfo.info
                },
                equipmentStatus: {
                    operationalCondition: currentPlan.passagePlan.equipmentStatus.operationalCondition,
                    failures: currentPlan.passagePlan.equipmentStatus.failures
                },
                weatherRouting: {
                    required: false,            
                    acknowledged: false,     
                    pilotPointAcknowledged: false 
                }  
            }
        } else {
            result!.route.passagePlan = {
                description: "",
                reminders: "",
                notEcdisCompliant: false,
                encCellsUsed: "",
                booksAndTables: "",
                chartsAndLightsNotes: "",
                sailingDirections: "",
                listOfRadioSignals: "",
                listOfLights: "",
                gpsEcdisRadarMap: "",
                paperCharts: "",
                pubCorrectedToWeek: "",
                tideTables: "",
                publicationsNotes: "",
                mastersOrders: "",
                captain: "",
                navigationalOfficer: "",
                planDate: "",
                safetyMessages: {
                    navareaRegions: "",
                    navtexStations: ""
                },
                weatherInfo: {
                    navareaRegions: "",
                    navtexStations: "",
                    info: ""
                },
                equipmentStatus: {
                    operationalCondition: "",
                    failures: ""
                },
                weatherRouting: {
                    required: false,            
                    acknowledged: false,     
                    pilotPointAcknowledged: false 
                } 
            }
        }
    }

    if (!result)
    {
        throw new Error("Unknown format");
    }
    
    return result;
}

export function importAndMerge(str: string, mergeWith?: VoyageRoute, filename?: string): ImportResult|null
{
    const imported = importAndGetFormat(str, filename);

    if (TraceService && imported.result != null)
    {
        TraceService.event(EVENT_CATEGORIES.ROUTE_IMPORT_EXPORT, "import", imported.format);
    }

    if (imported.result !== null && mergeWith)
    {
        // Find last isPassed index
        const lastIsPassed = findLastIndex(mergeWith.route_geometry, f => f.geometry.type === "Point" && f.properties.isPassed);

        let passedGeometry: RouteGeometry = [];
        if (lastIsPassed !== -1)
        {
            // Include LineString going away from last passed point to conserve leg type
            passedGeometry = mergeWith.route_geometry.slice(0, lastIsPassed+2);
        }

        const existingWaypoints = mergeWith.route_geometry.filter(g => g.geometry.type === "Point") as VoyageRoutePointFeature[];
        imported.result.route.route_geometry = mergeImported(passedGeometry, existingWaypoints, imported.result.route.route_geometry, imported.format === Format.SPOS);
    }

    // A route may only carry the two outermost pilot points. Enforce this after any merge,
    // since merging copies pilotPoint flags over from the existing route.
    if (imported.result !== null)
    {
        keepOutermostPilotPoints(imported.result.route.route_geometry);
    }

    return imported.result;
}

function importAndGetFormat(str: string, filename?: string): { result: ImportResult|null, format: Format }
{
    let result = JRCConverter.import(str);
    if (result) return { result, format: Format.JRC };

    result = SposConverter.import(str);
    if (result) return { result, format: Format.SPOS };

    result = NacosConverter.import(str);
    if (result) return { result, format: Format.Nacos };

    result = RTEConverter.import(str);
    if (result) return { result, format: Format.RTE };

    result = RTZConverter.import(str, { filename });
    if (result) {
        const rtzVersion = result.extras?.rtz_version as RTZVersion | undefined;
        switch (rtzVersion) {
            case RTZVersion.RTZ_1_0:
                return { result, format: Format.RTZ_1_0 };
            case RTZVersion.RTZ_1_1:
                return { result, format: Format.RTZ_1_1 };
            case RTZVersion.RTZ_1_2:
                return { result, format: Format.RTZ_1_2 };
            case RTZVersion.RTZ_2_0:
                return { result, format: Format.RTZ_2_0 };
            default:
                return { result, format: Format.RTZ_1_0 };
        }
    }

    result = SperryMVMConverter.import(str);
    if (result) return { result, format: Format.Sperry };

    result = VesselsPlanningConverter.import(str);
    if (result) return { result, format: Format.VoyagePlanning };

    return { result: null, format: Format.VoyagePlanning };
}

function findExistingPoint(points: VoyageRoutePointFeature[], coord: [number, number]): undefined|VoyageRoutePointFeature
{
    const margin = 0.00001; // Potential rounding error from conversions
    const found = points.filter(p => CoordinatesUtils.isCoordinatesEqual(p.geometry.coordinates, coord, margin));

    if (found.length === 1) return found[0];
}

export function mergeImported(passedRoute: RouteGeometry, existingPoints: VoyageRoutePointFeature[], imported: RouteGeometry,
                              isSPOS: boolean): RouteGeometry
{
    let lastPassedLineString: null|VoyageRouteLineFeature = null;

    if (passedRoute.length > 0)
    {
        // Pop ending LineString from passed route
        if (passedRoute[passedRoute.length-1].geometry.type === "LineString")
        {
            lastPassedLineString = <VoyageRouteLineFeature>passedRoute.pop();
        }

        // First prune imported route of any already passed features
        const passedPoints = <VoyageRoutePointFeature[]>passedRoute.filter( f => f.geometry.type === "Point");
        const pruneToIndex = findLastIndex(imported, feature => {
            if (feature.geometry.type === "Point")
            {
                return findExistingPoint(passedPoints, feature.geometry.coordinates) !== undefined;
            }
            return false;
        });

        if (pruneToIndex !== -1)
        {
            imported = imported.slice(pruneToIndex+1);

            // Modify first leg to start at last passed point
            (<VoyageRouteLineFeature>imported[0]).geometry.coordinates[0] = passedPoints[passedPoints.length-1].geometry.coordinates;
        }
        else
        {
            // Add leg between last passed point and first imported point
            const from = passedPoints[passedPoints.length-1].geometry.coordinates;
            const to = <[number,number]>imported[0].geometry.coordinates;
            imported.unshift({
                type: 'Feature',
                geometry: {
                    coordinates: [from, to],
                    type: 'LineString'
                },
                properties: {
                    leg_type: lastPassedLineString?.properties.leg_type || getLegTypeFromDist(from, to)
                }
        });
        }
    }

    // Merge point properties
    // Go through new route on coordinates, for each, try to find in existing route and copy properties over
    let lastMatchedPoint: VoyageRoutePointFeature|null = null; 
    for (let i = 0; i < imported.length; i++)
    {
        const feature = imported[i];
        if (feature.geometry.type === "Point")
        {
            const point = <VoyageRoutePointFeature>feature;
            const existingPoint = findExistingPoint(existingPoints, point.geometry.coordinates);
            if (existingPoint)
            {
                lastMatchedPoint = existingPoint;
                point.properties = {
                    ...point.properties,
                    ...existingPoint.properties
                };
            }
            else
            {
                // Set type for any new waypoints: Navigational for points from SPOS, Hard for everything else
                point.properties.type = isSPOS ? "Navigational" : "Hard";

                if (isSPOS && lastMatchedPoint)
                {
                    // Any inserted navigational points should inherit any speed limits previously set on that leg
                    let shouldCopyFrom: null|VoyageRoutePointFeature = null;
                    for (let j = i-1; j >= 0; j--)
                    {
                        if (imported[j].geometry.type === "Point")
                        {
                            const margin = 0.00001; // Potential rounding error from conversions
                            if (CoordinatesUtils.isCoordinatesEqual(lastMatchedPoint.geometry.coordinates, 
                                                                    (<VoyageRoutePointFeature>imported[j]).geometry.coordinates))
                            {
                                shouldCopyFrom = lastMatchedPoint;
                                break;
                            }
                            else if (imported[j].properties.type === "Hard")
                            {
                                // New leg
                                break;
                            }
                        }
                    }

                    if (shouldCopyFrom)
                    {
                        if (shouldCopyFrom.properties.speed_maximum !== undefined)
                        {
                            point.properties.speed_maximum = shouldCopyFrom.properties.speed_maximum;
                        }
                        if (shouldCopyFrom.properties.speed_minimum !== undefined)
                        {
                            point.properties.speed_minimum = shouldCopyFrom.properties.speed_minimum;
                        }
                    }
                }
            }
        }
    }

    return passedRoute.concat(imported);
}
